import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Building2 } from 'lucide-react';
import { CompanyFollowUp, FollowUpCompany } from '../types';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import FollowUpPanel from '../components/FollowUpPanel';

const LeadDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { canManage, isSales } = useAuth();

  const [company, setCompany] = useState<FollowUpCompany | null>(null);
  const [followUps, setFollowUps] = useState<CompanyFollowUp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getCompanyFollowUps(id);
      setCompany(data.company);
      setFollowUps(data.followUps);
    } catch (err: any) {
      console.error('Error loading follow-ups:', err);
      setError(err?.response?.data?.message || 'Gagal memuat data lead.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Kembali ke daftar dengan halaman dan filter yang sama. `navigate(-1)`
  // memulihkan query string apa adanya; `location.key === 'default'` menandai
  // entri riwayat pertama (mis. tautan yang dibuka langsung), di mana melangkah
  // mundur justru keluar dari aplikasi.
  const goBackToLeads = () => {
    if (location.key === 'default') navigate('/leads');
    else navigate(-1);
  };

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric' });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" text="Memuat data lead..." />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="space-y-4">
        <button onClick={goBackToLeads} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" /> Kembali ke Leads
        </button>
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-600">{error || 'Data tidak tersedia'}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button onClick={goBackToLeads} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
        <ArrowLeft className="h-4 w-4" /> Kembali ke Leads
      </button>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 rounded-md p-3 bg-primary-100">
            <Building2 className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{company.companyName}</h1>
            {company.brandName && <p className="text-sm text-gray-500">{company.brandName}</p>}
            <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
              <div className="flex gap-2">
                <dt className="text-gray-500">Owner:</dt>
                <dd className="text-gray-900">{company.ownerName || '-'}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-gray-500">Telepon:</dt>
                <dd className="text-gray-900">{company.phone || '-'}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-gray-500">Terdaftar:</dt>
                <dd className="text-gray-900">{formatDate(company.createdAt)}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <FollowUpPanel
        companyId={company.id}
        followUps={followUps}
        canSubmit={canManage || isSales}
        onSubmitted={load}
        trialPhase={company.trialPhase}
        messagePreview={company.followUpMessage}
        trialEndsAt={company.trialEndsAt}
        canExtendTrial={company.canExtendTrial}
        extendTrialBlockedReason={company.extendTrialBlockedReason}
        trialExtensions={company.trialExtensions}
      />
    </div>
  );
};

export default LeadDetail;
